import ReactMarkdown, { Components } from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import rehypeRaw from "rehype-raw";

export interface MarkdownProps {
  /**
   * The markdown content to render
   */
  children: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Size variant for text styling
   */
  size?: "xs" | "sm" | "base";
  /**
   * Customize specific markdown components
   */
  customComponents?: Partial<Components>;
}

export function Markdown({
  children,
  className = "",
  size = "sm",
  customComponents = {},
}: MarkdownProps) {
  // Size-specific class names
  const sizeClasses = {
    xs: {
      base: "text-xs",
      p: "my-1 text-xs",
      list: "pl-4 text-xs",
      listItem: "my-0.5 text-xs",
      h1: "my-2 text-sm font-bold",
      h2: "my-1.5 text-xs font-bold",
      h3: "my-1 text-xs font-bold",
      h4: "my-1 text-xs font-semibold",
      h5: "my-0.5 text-xs font-medium",
      h6: "my-0.5 text-xs font-normal",
      a: "text-xs text-blue-600",
      blockquote: "border-l-2 pl-2 text-xs",
      inlineCode: "text-xs",
      codeBlock: { fontSize: "0.6rem" },
    },
    sm: {
      base: "text-sm",
      p: "my-2 text-sm",
      list: "pl-6 text-sm",
      listItem: "my-1 text-sm",
      h1: "my-3 text-lg font-bold",
      h2: "my-2 text-base font-bold",
      h3: "my-2 text-sm font-bold",
      h4: "my-1.5 text-sm font-semibold",
      h5: "my-1 text-sm font-medium",
      h6: "my-1 text-sm font-normal",
      a: "text-sm text-blue-600",
      blockquote: "border-l-4 pl-4 text-sm",
      inlineCode: "text-xs",
      codeBlock: { fontSize: "0.7rem" },
    },
    base: {
      base: "text-base",
      p: "my-3 text-base",
      list: "pl-8 text-base",
      listItem: "my-1.5 text-base",
      h1: "my-4 text-xl font-bold",
      h2: "my-3 text-lg font-bold",
      h3: "my-2 text-base font-bold",
      h4: "my-2 text-base font-semibold",
      h5: "my-1.5 text-base font-medium",
      h6: "my-1 text-base font-normal",
      a: "text-base text-blue-600",
      blockquote: "border-l-4 pl-4 text-base",
      inlineCode: "text-sm",
      codeBlock: { fontSize: "0.8rem" },
    },
  };

  const sizeClass = sizeClasses[size];

  // Default component mappings with size-aware styling
  const defaultComponents: Components = {
    code(props) {
      const { children, className: codeClassName, node, ...rest } = props;
      const match = /language-(\w+)/.exec(codeClassName || "");
      const isInline = node?.position?.start.line === node?.position?.end.line;

      return !isInline ? (
        <div className="my-2 overflow-hidden rounded">
          <div className="flex items-center justify-between bg-slate-700 px-4 py-1.5">
            <span className="text-xs text-slate-300">
              {match ? match[1] : "code"}
            </span>
          </div>
          <SyntaxHighlighter
            language={match ? match[1] : ""}
            style={oneDark}
            customStyle={{ margin: 0, ...sizeClass.codeBlock }}
            PreTag="div"
          >
            {String(children).replace(/\n$/, "")}
          </SyntaxHighlighter>
        </div>
      ) : (
        <code
          className={`rounded bg-slate-100 px-1 py-0.5 ${sizeClass.inlineCode} dark:bg-slate-800`}
          {...rest}
        >
          {children}
        </code>
      );
    },
    p: ({ children }) => <p className={sizeClass.p}>{children}</p>,
    ul: ({ children }) => (
      <ul className={`my-2 list-disc ${sizeClass.list}`}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className={`my-2 list-decimal ${sizeClass.list}`}>{children}</ol>
    ),
    li: ({ children }) => <li className={sizeClass.listItem}>{children}</li>,
    h1: ({ children }) => <h1 className={sizeClass.h1}>{children}</h1>,
    h2: ({ children }) => <h2 className={sizeClass.h2}>{children}</h2>,
    h3: ({ children }) => <h3 className={sizeClass.h3}>{children}</h3>,
    h4: ({ children }) => <h4 className={sizeClass.h4}>{children}</h4>,
    h5: ({ children }) => <h5 className={sizeClass.h5}>{children}</h5>,
    h6: ({ children }) => <h6 className={sizeClass.h6}>{children}</h6>,
    a: ({ href, children }) => (
      <a
        href={href}
        className={`${sizeClass.a} hover:underline`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className={`my-2 border-gray-300 italic ${sizeClass.blockquote}`}
      >
        {children}
      </blockquote>
    ),
  };

  // Merge default components with custom components
  const components = { ...defaultComponents, ...customComponents };

  return (
    <ReactMarkdown
      className={`prose prose-${size} max-w-none ${sizeClass.base} dark:prose-invert ${className}`}
      components={components}
      rehypePlugins={[rehypeRaw]}
    >
      {children}
    </ReactMarkdown>
  );
}
